import { brand } from '../../../theme/themePrimitives';
import CustomModal from '../../modals';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

/**
 * @heading1: AddPipelineModal
 * @paragraph: A modal component for adding a new pipeline. Uses Formik for form handling
 * and integrates with Redux for pipeline creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const ActivatingAutomationModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values for the pipeline creation form.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    // Initial form values for the pipeline creation form
    const [selectedOption, setSelectedOption] = useState<'existing_and_upcoming' | 'upcoming'>('upcoming');
    const { loaders } = useSelector((state: RootState) => state.automation);

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Activating automation">
        <Typography variant="body1" align="center" my={1}>Please select appropriate Action for this automation</Typography>
        <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(e.target.value as 'existing_and_upcoming' | 'upcoming')}>
            <Box
                border={1}
                borderColor={selectedOption === 'upcoming' ? brand[300] : 'grey.300'}
                borderRadius={1}
                p={2}
                mb={2}
                bgcolor={selectedOption === 'upcoming' ? brand[50] : 'transparent'}
                color={selectedOption === 'upcoming' ? brand[300] : 'black'}
            >
                <FormControlLabel
                    value="upcoming"
                    control={<Radio />}
                    label={
                        <Box>
                            <Typography variant="body2" fontWeight="600" mb={1} color={selectedOption === 'upcoming' ? brand[300] : 'black'}>Run automation on upcoming deals on this stage</Typography>
                            <Typography variant="body2" color={selectedOption === 'upcoming' ? brand[300] : 'textSecondary'}>(Only the new upcoming deals of the selected stage will get automated)</Typography>
                        </Box>
                    }
                />
            </Box>
            <Box
                border={1}
                borderColor={selectedOption === 'existing_and_upcoming' ? brand[300] : 'grey.300'}
                borderRadius={1}
                p={2}
                bgcolor={selectedOption === 'existing_and_upcoming' ? brand[50] : 'transparent'}
                color={selectedOption === 'existing_and_upcoming' ? brand[300] : 'black'}
            >
                <FormControlLabel
                    value="existing_and_upcoming"
                    control={<Radio />}
                    label={
                        <Box>
                            <Typography variant="body2" fontWeight="600" mb={1} color={selectedOption === 'existing_and_upcoming' ? brand[300] : 'black'}>Run automation for existing and upcoming deals</Typography>
                            <Typography variant="body2" color={selectedOption === 'existing_and_upcoming' ? brand[300] : 'textSecondary'}>(All the existing and new deals of the selected stage will get automated)</Typography>
                        </Box>
                    }
                />
            </Box>
        </RadioGroup>
        <Box display="flex" gap={2} mt={3}>
            <Button variant="outlined" color="secondary" onClick={onClose} fullWidth>
                Cancel
            </Button>
            <Button variant="contained" color="primary" fullWidth>
                Activate
            </Button>
        </Box>
    </CustomModal>;
};

export default ActivatingAutomationModal;